
import { defineComponent } from 'vue';
import store from '@/store';
import {
  ManualCharge,
  ManualChargeFilters,
  ManualChargePagination,
  ManualChargeType,
} from '@/interfaces/panel/manual-charge.model';
import { PersianDate } from '@/Utils/persian-date';
import { AxiosUtil } from '@/Utils/axios.util';
import { API } from '@/constants/api';

interface StatusOption {
  label: string;
  value: ManualChargeFilters['status'];
  icon?: string;
  color: string;
}

interface ChargeTypeOption {
  label: string;
  value: ManualChargeType | 'ALL';
  icon?: string;
  color: string;
}

interface ReceiptDialogState {
  open: boolean;
  url: string;
  trackingCode: string;
  loading: boolean;
  previewUrl: string;
  error: string;
}

interface CryptoDetailsDialogState {
  open: boolean;
  charge: ManualCharge | null;
}

interface CreateCryptoDialogState {
  open: boolean;
  loading: boolean;
  form: {
    userId: string;
    amount: number;
    cryptoAmount: number;
    cryptoNetwork: string;
    txHash: string;
    senderAddress: string;
    receiverAddress: string;
    blockchainTxUrl: string;
    blockNumber: string;
    confirmations: number;
    description: string;
  };
}

export default defineComponent({
  name: 'ManualChargesPage',

  data() {
    return {
      statusOptions: [
        { label: 'همه وضعیت‌ها', value: 'ALL', icon: 'mdi-format-list-bulleted', color: 'primary' },
        { label: 'در انتظار تایید', value: 'PENDING', icon: 'mdi-timer-sand', color: 'warning' },
        { label: 'تایید شده', value: 'SUCCESS', icon: 'mdi-check-decagram', color: 'success' },
        { label: 'رد شده', value: 'REJECTED', icon: 'mdi-close-octagon', color: 'error' },
      ] as StatusOption[],
      chargeTypeOptions: [
        { label: 'همه', value: 'ALL', icon: 'mdi-wallet', color: 'primary' },
        { label: 'ریالی', value: 'RIALI', icon: 'mdi-cash', color: 'blue' },
        { label: 'کریپتو', value: 'CRYPTO', icon: 'mdi-bitcoin', color: 'orange' },
      ] as ChargeTypeOption[],
      pageSizeOptions: [10, 20, 50, 100] as number[],
      selectedStatus: 'ALL' as ManualChargeFilters['status'],
      selectedChargeType: 'ALL' as ManualChargeType | 'ALL',
      selectedPageSize: 20,
      searchQuery: '',
      receiptDialog: {
        open: false,
        url: '',
        trackingCode: '',
        loading: false,
        previewUrl: '',
        error: '',
      } as ReceiptDialogState,
      cryptoDetailsDialog: {
        open: false,
        charge: null,
      } as CryptoDetailsDialogState,
      createCryptoDialog: {
        open: false,
        loading: false,
        form: {
          userId: '',
          amount: 0,
          cryptoAmount: 0,
          cryptoNetwork: 'TRC20',
          txHash: '',
          senderAddress: '',
          receiverAddress: '',
          blockchainTxUrl: '',
          blockNumber: '',
          confirmations: 0,
          description: '',
        },
      } as CreateCryptoDialogState,
      approveDialog: {
        open: false,
        id: '',
        trackingCode: '',
        amount: 0,
        editableAmount: 0,
        editableCryptoAmount: 0,
        chargeType: 'RIALI' as ManualChargeType,
        cryptoNetwork: '',
        userName: '',
        loading: false,
      },
      rejectDialog: {
        open: false,
        id: '',
        amount: 0,
        cryptoAmount: 0,
        chargeType: 'RIALI' as ManualChargeType,
        userName: '',
        loading: false,
      },
      persianDate: new PersianDate(),
    };
  },

  computed: {
    manualCharges(): ManualCharge[] {
      return store.getters['panel/getManualCharges'];
    },
    displayedManualCharges(): ManualCharge[] {
      return this.filteredManualCharges;
    },
    manualChargesPagination(): ManualChargePagination {
      return store.getters['panel/getManualChargesPagination'];
    },
    manualChargesFilters(): ManualChargeFilters {
      return store.getters['panel/getManualChargesFilters'];
    },
    manualChargesLoading(): boolean {
      return store.getters['panel/getManualChargesLoading'];
    },
    manualChargeActionLoading(): Record<string, boolean> {
      return store.getters['panel/getManualChargeActionLoading'];
    },
    totalItems(): number {
      return this.manualChargesPagination.totalItems ?? this.manualCharges.length;
    },
    totalItemsFormatted(): string {
      return new Intl.NumberFormat('fa-IR').format(this.totalItems);
    },
    filteredManualCharges(): ManualCharge[] {
      const query = this.searchQuery?.trim().toLowerCase();
      if (!query) {
        return this.manualCharges;
      }

      return this.manualCharges.filter((charge) => {
        const values: Array<string | null | undefined> = [
          charge.id,
          charge.trackingCode,
          charge.user?.name,
          charge.user?.email,
          charge.user?.mail,
        ];

        return values.some((value) => {
          if (!value) {
            return false;
          }
          return value.toLowerCase().includes(query);
        });
      });
    },
    filterSummary(): string {
      if (this.totalItems === 0) {
        return 'داده‌ای برای نمایش وجود ندارد.';
      }

      const formatNumber = (value: number): string => new Intl.NumberFormat('fa-IR').format(value);
      const parts = [`نمایش ${formatNumber(this.displayedManualCharges.length)} از ${formatNumber(this.totalItems)} نتیجه`];

      if (this.selectedStatus !== 'ALL') {
        const statusLabel = this.statusOptions.find((option) => option.value === this.selectedStatus)?.label ?? '';
        if (statusLabel) {
          parts.push(`وضعیت: ${statusLabel}`);
        }
      }

      if (this.selectedChargeType !== 'ALL') {
        const chargeTypeLabel = this.chargeTypeOptions.find((option) => option.value === this.selectedChargeType)?.label ?? '';
        if (chargeTypeLabel) {
          parts.push(`نوع: ${chargeTypeLabel}`);
        }
      }

      if (this.searchQuery.trim()) {
        parts.push(`جستجو: «${this.searchQuery.trim()}»`);
      }

      return parts.join(' | ');
    },
    emptyStateMessage(): string {
      if (this.manualCharges.length === 0) {
        return 'هنوز درخواستی برای شارژ دستی ثبت نشده است.';
      }

      if (this.searchQuery.trim() || this.selectedStatus !== 'ALL') {
        return 'نتیجه‌ای مطابق با فیلترها یا جستجوی فعلی یافت نشد.';
      }

      return 'داده‌ای برای نمایش وجود ندارد.';
    },
    hasActiveFilters(): boolean {
      return (
        this.selectedStatus !== 'ALL'
        || this.selectedChargeType !== 'ALL'
        || Boolean(this.searchQuery.trim())
        || this.selectedPageSize !== 20
      );
    },

    receiptImageSource(): string {
      return this.receiptDialog.previewUrl || this.receiptDialog.url || '';
    },

    receiptHasImage(): boolean {
      return Boolean(this.receiptImageSource);
    },

    amountMismatch(): boolean {
      if (!this.approveDialog.editableAmount) {
        return false;
      }
      return this.approveDialog.editableAmount !== this.approveDialog.amount;
    },

    isApproveAmountValid(): boolean {
      if (this.approveDialog.chargeType === 'CRYPTO') {
        const value = this.approveDialog.editableCryptoAmount;
        return typeof value === 'number' && !isNaN(value) && value > 0;
      } else {
        const value = this.approveDialog.editableAmount;
        return typeof value === 'number' && !isNaN(value) && value > 0;
      }
    },

    formattedApproveAmount(): string {
      const value = this.approveDialog.editableAmount;
      if (!value || typeof value !== 'number' || isNaN(value)) {
        return '';
      }
      return new Intl.NumberFormat('fa-IR').format(value);
    },

    formattedApproveCryptoAmount(): string {
      const value = this.approveDialog.editableCryptoAmount;
      if (!value || typeof value !== 'number' || isNaN(value)) {
        return '';
      }
      return new Intl.NumberFormat('fa-IR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      }).format(value);
    },
  },

  watch: {
    manualChargesFilters: {
      handler(newFilters: ManualChargeFilters) {
        if (this.selectedStatus !== newFilters.status) {
          this.selectedStatus = newFilters.status;
        }
        if (this.selectedChargeType !== newFilters.chargeType) {
          this.selectedChargeType = newFilters.chargeType ?? 'ALL';
        }
        if (this.selectedPageSize !== newFilters.pageSize) {
          this.selectedPageSize = newFilters.pageSize;
        }
      },
      deep: true,
    },
  },

  created() {
    this.selectedStatus = this.manualChargesFilters.status;
    this.selectedChargeType = this.manualChargesFilters.chargeType ?? 'ALL';
    this.selectedPageSize = this.manualChargesFilters.pageSize;
    this.refreshList({
      status: this.selectedStatus,
      chargeType: this.selectedChargeType,
      page: this.manualChargesFilters.page,
      pageSize: this.selectedPageSize,
    });
  },

  beforeUnmount() {
    this.cleanupReceiptPreview();
  },

  methods: {
    refreshList(overrides: Partial<ManualChargeFilters> = {}): void {
      const payload: Partial<ManualChargeFilters> = {
        status: overrides.status ?? this.selectedStatus,
        chargeType: overrides.chargeType ?? this.selectedChargeType,
        page: overrides.page ?? this.manualChargesFilters.page,
        pageSize: overrides.pageSize ?? this.selectedPageSize,
      };

      if (payload.status) {
        this.selectedStatus = payload.status;
      }
      if (payload.chargeType) {
        this.selectedChargeType = payload.chargeType;
      }
      if (payload.pageSize) {
        this.selectedPageSize = payload.pageSize;
      }

      store.dispatch('panel/getManualChargesRequest', payload);
    },
    openRejectDialog(charge: ManualCharge): void {
      this.rejectDialog = {
        open: true,
        id: charge.id,
        amount: charge.amount,
        cryptoAmount: charge.cryptoAmount ?? 0,
        chargeType: charge.chargeType,
        userName: charge.user?.name ?? 'کاربر',
        loading: false,
      };
    },

    closeRejectDialog(): void {
      this.rejectDialog.open = false;
      this.rejectDialog.loading = false;
    },

    async confirmReject(): Promise<void> {
      if (!this.rejectDialog.id) {
        return;
      }
      this.rejectDialog.loading = true;
      try {
        await store.dispatch('panel/rejectManualChargeRequest', this.rejectDialog.id);
        this.closeRejectDialog();
      } catch (error) {
        console.error('reject manual charge error ===>', error);
        this.rejectDialog.loading = false;
      }
    },

    isRejecting(manualChargeId: string): boolean {
      return Boolean(this.manualChargeActionLoading[manualChargeId]);
    },

    handleStatusChange(value: ManualChargeFilters['status']): void {
      this.refreshList({ status: value, page: 1, pageSize: this.selectedPageSize });
    },

    handleStatusChipChange(value: ManualChargeFilters['status']): void {
      if (value && value !== this.selectedStatus) {
        this.handleStatusChange(value);
      }
    },

    handleChargeTypeChange(value: ManualChargeType | 'ALL'): void {
      this.refreshList({ chargeType: value, page: 1, pageSize: this.selectedPageSize });
    },

    handleChargeTypeChipChange(value: ManualChargeType | 'ALL'): void {
      if (value && value !== this.selectedChargeType) {
        this.handleChargeTypeChange(value);
      }
    },

    handlePageSizeChange(value: number): void {
      this.refreshList({ pageSize: value, page: 1, status: this.selectedStatus });
    },

    handlePageChange(page: number): void {
      this.refreshList({ page });
    },

    openReceipt(charge: ManualCharge): void {
      this.cleanupReceiptPreview();
      const resolvedUrl = this.resolveReceiptUrl(charge.receipt ?? '');

      this.receiptDialog.open = true;
      this.receiptDialog.url = resolvedUrl;
      this.receiptDialog.previewUrl = resolvedUrl;
      this.receiptDialog.trackingCode = charge.trackingCode ?? '';
      this.receiptDialog.error = '';
      this.receiptDialog.loading = Boolean(resolvedUrl);

      if (resolvedUrl) {
        void this.loadReceiptPreview(resolvedUrl);
      } else {
        this.receiptDialog.loading = false;
        this.receiptDialog.error = 'تصویری برای این رسید در دسترس نیست.';
      }
    },

    closeReceipt(): void {
      this.cleanupReceiptPreview();
      this.receiptDialog.open = false;
      this.receiptDialog.url = '';
      this.receiptDialog.previewUrl = '';
      this.receiptDialog.trackingCode = '';
      this.receiptDialog.loading = false;
      this.receiptDialog.error = '';
    },

    openApproveDialog(charge: ManualCharge): void {
      if (charge.chargeType === 'CRYPTO') {
        this.approveDialog = {
          open: true,
          id: charge.id,
          trackingCode: charge.trackingCode ?? '',
          amount: charge.amount,
          editableAmount: charge.amount,
          editableCryptoAmount: charge.cryptoAmount ?? 0,
          chargeType: 'CRYPTO',
          cryptoNetwork: charge.cryptoNetwork ?? '',
          userName: charge.user?.name ?? 'کاربر',
          loading: false,
        };
      } else {
        this.approveDialog = {
          open: true,
          id: charge.id,
          trackingCode: charge.trackingCode ?? '',
          amount: charge.amount,
          editableAmount: charge.amount,
          editableCryptoAmount: 0,
          chargeType: 'RIALI',
          cryptoNetwork: '',
          userName: charge.user?.name ?? 'کاربر',
          loading: false,
        };
      }
    },

    closeApproveDialog(): void {
      this.approveDialog.open = false;
      this.approveDialog.loading = false;
      this.approveDialog.editableAmount = 0;
      this.approveDialog.editableCryptoAmount = 0;
    },

    async confirmApprove(): Promise<void> {
      if (!this.approveDialog.id) {
        return;
      }
      this.approveDialog.loading = true;
      try {
        let amount: number;

        if (this.approveDialog.chargeType === 'CRYPTO') {
          // برای کریپتو، مقدار کریپتو را ارسال می‌کنیم
          amount = this.approveDialog.editableCryptoAmount;
          console.log('🔍 Debug - Crypto charge approval');
          console.log('🔍 Debug - cryptoAmount:', amount);
        } else {
          // برای ریالی، مبلغ تومان را ارسال می‌کنیم
          amount = this.approveDialog.editableAmount;
          console.log('🔍 Debug - Riali charge approval');
          console.log('🔍 Debug - amount (Toman):', amount);
        }

        console.log('🔍 Debug - final amount to send:', amount);
        console.log('🔍 Debug - charge type:', this.approveDialog.chargeType);

        await store.dispatch('panel/approveManualChargeRequest', {
          manualChargeId: this.approveDialog.id,
          amount,
        });
        this.closeApproveDialog();
      } catch (error) {
        console.error('approve manual charge error ===>', error);
        this.approveDialog.loading = false;
      }
    },

    isApproving(manualChargeId: string): boolean {
      return Boolean(this.manualChargeActionLoading[manualChargeId]);
    },

    statusLabel(status: ManualCharge['status']): string {
      switch (status) {
        case 'PENDING':
          return 'در انتظار تایید';
        case 'SUCCESS':
          return 'تایید شده';
        case 'REJECTED':
          return 'رد شده';
        default:
          return status;
      }
    },

    statusColor(status: ManualCharge['status']): string {
      switch (status) {
        case 'PENDING':
          return 'warning';
        case 'SUCCESS':
          return 'success';
        case 'REJECTED':
          return 'error';
        default:
          return 'primary';
      }
    },

    chargeTypeLabel(chargeType: ManualChargeType): string {
      switch (chargeType) {
        case 'RIALI':
          return 'ریالی';
        case 'CRYPTO':
          return 'کریپتو';
        default:
          return chargeType;
      }
    },

    chargeTypeColor(chargeType: ManualChargeType): string {
      switch (chargeType) {
        case 'RIALI':
          return 'blue';
        case 'CRYPTO':
          return 'orange';
        default:
          return 'primary';
      }
    },

    resetFilters(): void {
      const defaults: ManualChargeFilters = {
        status: 'ALL',
        chargeType: 'ALL',
        page: 1,
        pageSize: 20,
      };

      this.searchQuery = '';
      this.selectedStatus = defaults.status;
      this.selectedChargeType = defaults.chargeType ?? 'ALL';
      this.selectedPageSize = defaults.pageSize;
      this.refreshList(defaults);
    },

    formatAmount(amount: number | string | null | undefined): string {
      if (amount === null || amount === undefined) {
        return '—';
      }
      const numeric = typeof amount === 'string' ? Number(amount) : amount;
      if (Number.isNaN(numeric)) {
        return '—';
      }
      return new Intl.NumberFormat('fa-IR').format(numeric);
    },

    formatDate(date: string | null | undefined): string {
      if (!date) {
        return '—';
      }
      return this.persianDate.convert(date, true);
    },

    resolveReceiptUrl(rawUrl: string): string {
      if (!rawUrl) {
        return '';
      }

      if (/^https?:\/\//i.test(rawUrl)) {
        return rawUrl;
      }

      let assetBase = window.location.origin;

      try {
        assetBase = new URL(API.panel.manualCharges.list).origin;
      } catch (error) {
        console.warn('manual charge asset base parse error ===>', error);
      }

      try {
        return new URL(rawUrl, assetBase).toString();
      } catch (error) {
        console.warn('resolve receipt url error ===>', error);
        return rawUrl;
      }
    },

    async loadReceiptPreview(url: string): Promise<void> {
      try {
        const response = await AxiosUtil.get(url, { responseType: 'blob' });
        if (response?.data instanceof Blob) {
          const blobUrl = URL.createObjectURL(response.data);
          this.cleanupReceiptPreview();
          this.receiptDialog.previewUrl = blobUrl;
        } else if (!this.receiptDialog.previewUrl) {
          this.receiptDialog.error = 'تصویر رسید دریافت نشد.';
        }
      } catch (error) {
        console.error('load receipt image error ===>', error);
        if (!this.receiptDialog.previewUrl) {
          this.receiptDialog.error = 'بارگذاری تصویر رسید با خطا مواجه شد.';
        }
      } finally {
        this.receiptDialog.loading = false;
      }
    },

    cleanupReceiptPreview(): void {
      if (this.receiptDialog.previewUrl && this.receiptDialog.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.receiptDialog.previewUrl);
      }
      this.receiptDialog.previewUrl = '';
    },

    openReceiptInNewTab(): void {
      const source = this.receiptImageSource;
      if (source) {
        window.open(source, '_blank', 'noopener');
      }
    },

    openCryptoDetails(charge: ManualCharge): void {
      this.cryptoDetailsDialog.open = true;
      this.cryptoDetailsDialog.charge = charge;
    },

    closeCryptoDetails(): void {
      this.cryptoDetailsDialog.open = false;
      this.cryptoDetailsDialog.charge = null;
    },

    openBlockchainExplorer(url?: string | null): void {
      if (url) {
        window.open(url, '_blank', 'noopener');
      }
    },

    openCreateCryptoDialog(): void {
      this.createCryptoDialog.open = true;
    },

    closeCreateCryptoDialog(): void {
      this.createCryptoDialog.open = false;
      this.createCryptoDialog.loading = false;
      // Reset form
      this.createCryptoDialog.form = {
        userId: '',
        amount: 0,
        cryptoAmount: 0,
        cryptoNetwork: 'TRC20',
        txHash: '',
        senderAddress: '',
        receiverAddress: '',
        blockchainTxUrl: '',
        blockNumber: '',
        confirmations: 0,
        description: '',
      };
    },

    async submitCreateCrypto(): Promise<void> {
      // Validate form
      if (!this.createCryptoDialog.form.userId) {
        store.dispatch('app/updateToastModal', {
          status: true,
          type: 'danger',
          message: 'شناسه کاربر الزامی است',
        }, { root: true });
        return;
      }

      if (!this.createCryptoDialog.form.cryptoAmount || this.createCryptoDialog.form.cryptoAmount <= 0) {
        store.dispatch('app/updateToastModal', {
          status: true,
          type: 'danger',
          message: 'مقدار کریپتو باید بیشتر از صفر باشد',
        }, { root: true });
        return;
      }

      if (!this.createCryptoDialog.form.amount || this.createCryptoDialog.form.amount <= 0) {
        store.dispatch('app/updateToastModal', {
          status: true,
          type: 'danger',
          message: 'معادل ریالی باید بیشتر از صفر باشد',
        }, { root: true });
        return;
      }

      if (!this.createCryptoDialog.form.txHash || this.createCryptoDialog.form.txHash.length < 10) {
        store.dispatch('app/updateToastModal', {
          status: true,
          type: 'danger',
          message: 'Transaction Hash معتبر نیست',
        }, { root: true });
        return;
      }

      this.createCryptoDialog.loading = true;

      try {
        const payload = {
          userId: this.createCryptoDialog.form.userId,
          amount: this.createCryptoDialog.form.amount,
          cryptoAmount: this.createCryptoDialog.form.cryptoAmount,
          cryptoNetwork: this.createCryptoDialog.form.cryptoNetwork,
          txHash: this.createCryptoDialog.form.txHash,
          senderAddress: this.createCryptoDialog.form.senderAddress || undefined,
          receiverAddress: this.createCryptoDialog.form.receiverAddress || undefined,
          blockchainTxUrl: this.createCryptoDialog.form.blockchainTxUrl || undefined,
          blockNumber: this.createCryptoDialog.form.blockNumber || undefined,
          confirmations: this.createCryptoDialog.form.confirmations || undefined,
          description: this.createCryptoDialog.form.description || undefined,
        };

        await store.dispatch('panel/createCryptoManualChargeRequest', payload);
        this.closeCreateCryptoDialog();
      } catch (error) {
        console.error('submit create crypto error ===>', error);
        this.createCryptoDialog.loading = false;
      }
    },
  },
});
